<template>
  <q-card flat bordered>
    <div class="q-pa-md">
      <div class="row justify-between">
        <div class="q-my-auto items-center row q-gutter-x-sm">
          <div>
            <div class="text-h6">Organization Members</div>
            <div style="font-size: 12px">Manage your organization members and their roles.</div>
          </div>
          <q-chip
            v-if="organization?.connection_id && $q.screen.gt.sm"
            icon="check_circle"
            color="green"
            dense
            text-color="white"
          >
            Single Sign-On
            <q-tooltip> Your organization utilizes single sign-on for access. </q-tooltip>
          </q-chip>
        </div>
        <div class="row items-center q-gutter-sm">
          <q-btn icon="refresh" flat @click="refreshOrganizationMembers" />
          <q-btn icon="download" flat @click="downloadMembers" />
          <q-input v-model="searchTerm" dense filled placeholder="Search users...">
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>
          <div v-if="organization?.products?.length">
            <q-btn
              id="intercom-invite-people"
              icon="add"
              :label="$q.screen.gt.sm ? 'Invite Users' : ''"
              unelevated
              color="primary"
              no-caps
              :loading="invitePeopleLoading"
              @click="invitePeopleDialog = true"
            />
          </div>
        </div>
      </div>
    </div>
    <q-markup-table v-if="!organizationMembers.length" bordered dense>
      <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
        <tr>
          <th scope="col"></th>
          <th v-for="n in 5" :key="n" scope="col">
            <q-skeleton animation="blink" type="text" />
          </th>
          <th scope="col"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="n in 4" :key="n" style="height: 43px">
          <td style="width: 101px">
            <q-skeleton type="circle" style="width: 28px; height: 28px" />
          </td>
          <td v-for="x in 5" :key="x">
            <q-skeleton animation="blink" type="text" />
          </td>
          <td style="width: 99px">
            <div class="row justify-end">
              <q-skeleton animation="blink" type="circle" style="width: 28px; height: 28px" />
            </div>
          </td>
        </tr>
      </tbody>
    </q-markup-table>
    <q-table
      v-else
      flat
      color="primary"
      row-key="id"
      dense
      bordered
      :filter="searchTerm"
      :rows="organizationMembers"
      :columns="memberColumns"
      :pagination="{
        rowsPerPage: 10
      }"
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #body-cell-picture="{ row }">
        <q-td>
          <q-avatar size="28px" round class="q-mr-sm bg-grey-4">
            <q-img
              v-if="row.id !== row.email"
              :src="row.picture"
              referrerpolicy="no-referrer"
              no-spinner
            />
            <q-icon v-else name="mail_outline">
              <q-tooltip>Invite Sent</q-tooltip>
            </q-icon>
          </q-avatar>
        </q-td>
      </template>
      <template #body-cell-actions="{ row }">
        <q-td align="right" style="height: 42px">
          <q-btn
            v-if="row.id === row.email"
            round
            dense
            unelevated
            text-color="primary"
            @click="resendInvite(row)"
          >
            <q-icon name="mail_outline" size="medium" color="primary" />
            <q-tooltip>Resend Invite</q-tooltip>
          </q-btn>
          <q-btn
            v-if="row.id !== organization?.owner_user_id && !organization?.use_security_groups"
            round
            dense
            unelevated
            @click="validateChangeMemberRole(row)"
          >
            <q-icon name="security" size="medium" />
            <q-tooltip class="bg-accent">Change Role</q-tooltip>
          </q-btn>
          <q-btn
            v-if="row.id !== $auth0.user.value?.sub && row.id !== organization?.owner_user_id"
            round
            dense
            unelevated
            @click="validateRemoveMember(row)"
          >
            <q-tooltip class="bg-red">Remove</q-tooltip>
            <q-icon name="delete" size="medium" color="red" />
          </q-btn>
        </q-td>
      </template>
    </q-table>
    <q-dialog v-model="invitePeopleDialog" transition-duration="100">
      <q-card style="width: 600px">
        <q-card-section class="flex justify-between">
          <div class="flex items-center">
            <q-icon size="md" name="mail_outline" class="q-mr-sm" />
            <div class="text-h6">Invite people to {{ organization?.display_name }}</div>
          </div>
          <q-btn v-close-popup icon="close" round dense unelevated />
        </q-card-section>
        <q-separator />
        <q-card-section>
          <div class="q-mb-sm text-caption">
            Invite people to your organization by entering their email addresses below. Press Tab or
            Enter to add another email address.
          </div>
          <email-select :emails="emails" @update-emails="updateEmails" />
          <q-card
            v-if="billedPerSeat"
            bordered
            flat
            :class="{ 'bg-deep-orange-2': remainingSeats < 0 }"
            class="q-mt-sm"
          >
            <div class="row justify-between q-pa-md" style="height: 64px">
              <div class="q-my-auto" :class="{ 'text-deep-orange-10': remainingSeats < 0 }">
                Remaining Seats: {{ Math.max(0, remainingSeats) }}
              </div>
              <div class="flex q-my-auto">
                <q-btn
                  v-if="remainingSeats < 0"
                  color="deep-orange-10 q-px-sm"
                  dense
                  outline
                  no-caps
                  @click="dialogPurchaseSeats"
                >
                  <q-icon name="warning" class="q-mr-sm" />
                  Purchase {{ Math.abs(remainingSeats) }} additional seat{{
                    Math.abs(remainingSeats) > 1 ? 's' : ''
                  }}
                </q-btn>
              </div>
            </div>
          </q-card>
        </q-card-section>
        <q-separator />
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            no-caps
            unelevated
            label="Invite"
            color="primary"
            :disable="emails.length === 0 || (remainingSeats < 0 && billedPerSeat)"
            @click="inviteEmails"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-model="removeMemberDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <span class="q-ml-sm">
            Are you sure you want to permanently remove <b>{{ toRemove?.email }}</b> from
            {{ organization?.display_name }}?

            <q-card-section class="q-gutter-sm">
              <div class="q-mx-md q-mt-md text-caption">
                You can transfer their Reports, Market Reports, Property Edits, and Datasets from
                the Query Builder if you wish.
              </div>
              <q-toggle
                v-model="isTransferringUserData"
                label="Transfer data"
                checked-icon="check"
                color="green"
                unchecked-icon="red"
              />

              <filter-by-owner
                v-if="isTransferringUserData"
                v-model="toTransfer"
                label="New Report Owner"
                :all-owners="organizationMembers.filter((_) => _.id !== toRemove.id)"
              />
            </q-card-section>
          </span>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            no-caps
            unelevated
            label="Remove"
            color="red"
            :disabled="isTransferringUserData && toTransfer === null"
            @click="removeFromOrganization"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-model="changeMemberRoleDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section>
          <div>
            Change the role of <b>{{ changeMemberRoleUser?.email }}</b
            >?
          </div>
          <div class="q-py-lg">Select a new role</div>
          <q-list class="q-gutter-md">
            <q-item v-ripple tag="label" class="q-px-none q-my-none">
              <q-item-section avatar>
                <q-radio v-model="changeMemberNewRole" val="owner" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Admin</q-item-label>
                <q-item-label caption>
                  Has full administrative access to the entire organization.
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-ripple tag="label" class="q-px-none q-my-none">
              <q-item-section avatar>
                <q-radio v-model="changeMemberNewRole" val="member" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Member</q-item-label>
                <q-item-label caption>
                  Can see every member in the organization and create new property reports.
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn no-caps unelevated label="Change Role" color="primary" @click="changeMemberRole" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script lang="ts">
import { useUserStore } from '@/stores/user'
import { useStripeStore } from '@/stores/stripe'
import { type UserAttributes } from 'common'
import { formatDistance } from 'date-fns'
import { formatDate } from '@/utils'
import { type QTableColumn, exportFile } from 'quasar'
import EmailSelect from '@/components/EmailSelect.vue'
import FilterByOwner from '@/components/FilterByOwner.vue'

export default {
  name: 'OrganizationMembers',
  components: { EmailSelect, FilterByOwner },
  data() {
    return {
      totalSeatAmount: 0,
      emails: [] as string[],
      searchTerm: '',
      invitePeopleDialog: false,
      invitePeopleLoading: false,
      changeMemberRoleDialog: false,
      changeMemberNewRole: null as 'member' | 'owner' | null,
      changeMemberRoleUser: null as UserAttributes | null,
      removeMemberDialog: false,
      toRemove: null as UserAttributes | null,
      toTransfer: null as UserAttributes | null,
      isTransferringUserData: true
    }
  },
  computed: {
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    propertyReportSeats() {
      return useStripeStore().propertyReportSeats
    },
    organization() {
      return useUserStore().organization
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    },
    remainingSeats() {
      return this.propertyReportSeats - this.organizationMembers.length - this.emails.length
    },
    memberColumns() {
      return [
        { field: 'picture', name: 'picture', label: '' },
        {
          field: 'given_name',
          name: 'given_name',
          label: 'First Name',
          sortable: true,
          align: 'left'
        },
        {
          field: 'family_name',
          name: 'family_name',
          label: 'Last Name',
          sortable: true,
          align: 'left'
        },
        { field: 'email', name: 'email', label: 'Email', sortable: true, align: 'left' },
        {
          field: (row: UserAttributes) => {
            return row.id === this.organization?.owner_user_id
              ? 'Owner'
              : this.organization?.admin_user_ids?.includes(row.id)
                ? 'Admin'
                : 'Member'
          },
          name: 'role',
          label: 'Role',
          sortable: true,
          align: 'left'
        },
        {
          field: 'created_on',
          name: 'created_on',
          label: 'Created',
          sortable: true,
          align: 'left',
          format: (val, row) =>
            row.id === row.email ? `Pending (${formatDistance(val, new Date())})` : formatDate(val)
        },
        {
          name: 'actions',
          label: ''
        }
      ] as QTableColumn[]
    }
  },
  watch: {
    organization() {
      this.totalSeatAmount = useStripeStore().propertyReportSeats
    }
  },
  mounted() {
    this.totalSeatAmount = useStripeStore().propertyReportSeats
  },
  methods: {
    formatDistance,
    downloadMembers() {
      exportFile(
        'hello-data-organization-members.csv',
        'First Name,Last Name,Email,Created\n' +
          this.organizationMembers
            .map(
              (member) =>
                `${member.given_name},${member.family_name},${member.email},${member.created_on}`
            )
            .join('\n')
      )
    },
    resendInvite(user: UserAttributes) {
      useUserStore().resendInviteEmail(user.email)
    },
    validateChangeMemberRole(row: UserAttributes) {
      this.changeMemberRoleDialog = true
      this.changeMemberRoleUser = row
    },
    validateRemoveMember(row: UserAttributes) {
      this.removeMemberDialog = true
      this.toRemove = row
      this.isTransferringUserData = true
      this.toTransfer = this.organizationMembers.find((_) => _.id === useUserStore().user.sub)
    },
    updateEmails(emails: string[]) {
      this.emails = emails.filter(
        (email) =>
          !this.organizationMembers.find((_) => _.email.toLowerCase() === email.toLowerCase())
      )
    },
    inviteEmails() {
      if (this.emails.length) {
        this.invitePeopleLoading = true
        useUserStore()
          .invitePeople(this.emails)
          .finally(() => {
            this.emails = []
            this.invitePeopleLoading = false
          })
      }
    },
    dialogPurchaseSeats() {
      this.invitePeopleDialog = false
      this.totalSeatAmount = this.propertyReportSeats + Math.abs(this.remainingSeats)
      this.$nextTick(() => {
        const el = document.getElementById('scroll-anchor')
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },
    changeMemberRole() {
      if (this.changeMemberNewRole) {
        Promise.all([
          useUserStore().initOrganization(false),
          useUserStore().initOrganizationMembers(false)
        ]).then(() => {
          if (this.changeMemberRoleUser) {
            const { id, email } = this.changeMemberRoleUser
            let changeId = this.organizationMembers.find((_) => _.id === id)?.id
            if (!changeId) {
              changeId = this.organizationMembers.find(
                (_) => _.email.toLowerCase() === email.toLowerCase()
              )?.id
            }
            if (changeId) {
              const newOwners =
                this.organization?.admin_user_ids.filter((_) => _ !== changeId) || []
              if (this.changeMemberNewRole === 'owner') {
                newOwners.push(changeId)
              }
              if (this.organization?.id) {
                useUserStore().updateOrganization(this.organization.id, {
                  display_name: this.organization.display_name,
                  owner_user_id: this.organization.owner_user_id,
                  admin_user_ids: newOwners,
                  connection_id: this.organization.connection_id,
                  logo_url: this.organization.logo_url,
                  report_tags: this.organization.report_tags,
                  use_security_groups: this.organization.use_security_groups,
                  sections: this.organization.sections,
                  columns: this.organization.columns
                })
              }
            }
          }
          this.changeMemberRoleDialog = false
          this.changeMemberRoleUser = null
          this.changeMemberNewRole = null
        })
      }
    },
    removeFromOrganization() {
      if (this.toRemove) {
        this.$tracker.organizationMemberRemoval(this.isTransferringUserData)

        // The button is disabled if they are transfering reports
        // and they haven't selected someone
        const transferOwnerId = this.isTransferringUserData ? this.toTransfer.id : undefined
        useUserStore().removeFromOrganization(this.toRemove.id, transferOwnerId)
        this.toRemove = null
        this.toTransfer = null
        this.isTransferringUserData = true
      }
    },
    refreshOrganizationMembers() {
      useUserStore().initOrganization(false)
      useUserStore().initOrganizationMembers()
    }
  }
}
</script>
<style scoped lang="scss">
.width-150 {
  width: 150px;
}
</style>
